import Image from 'next/image';
import { forwardRef } from 'react';

import { useBrand } from '@/hooks/use-brand';

export type BrandLogoProps = {
  variant?: 'logo' | 'icon' | 'white-logo' | 'white-icon';
  format?: 'png' | 'svg';
  width?: number;
  height?: number;
  className?: string;
  alt?: string;
  priority?: boolean;
  fill?: boolean;
};

export const BrandLogo = forwardRef<HTMLImageElement, BrandLogoProps>((props, ref) => {
  const { brandConfig, getAsset } = useBrand();
  const variant = props.variant || 'logo';

  // Get the appropriate asset path
  const assetPath = getAsset(variant, props.format || 'svg');

  // Generate appropriate alt text if not provided
  const altText = props.alt || `${brandConfig.name} ${variant.replace('-', ' ')}`;

  const width = props.fill ? undefined : props.width || (variant.includes('icon') ? 50 : 200);
  const height = props.fill ? undefined : props.height || 50;

  return (
    <Image
      ref={ref}
      src={assetPath}
      alt={altText}
      width={width}
      height={height}
      className={props.className}
      priority={props.priority}
      fill={props.fill}
    />
  );
});

/**
 * Quick access components for common logo variants
 */
export const MainLogo: React.FC<Omit<BrandLogoProps, 'variant'>> = (props) => (
  <BrandLogo variant="logo" {...props} />
);

export const MainIcon: React.FC<Omit<BrandLogoProps, 'variant'>> = (props) => (
  <BrandLogo variant="icon" {...props} />
);

export const WhiteLogo: React.FC<Omit<BrandLogoProps, 'variant'>> = (props) => (
  <BrandLogo variant="white-logo" {...props} />
);

export const WhiteIcon: React.FC<Omit<BrandLogoProps, 'variant'>> = (props) => (
  <BrandLogo variant="white-icon" {...props} />
);
